/**
 * @description Hook that manages Tokenex SDK integration:
 * - Loads the Tokenex SDK script
 * - Handles initialization
 * - Generates configuration objects for card and CVV fields
 *
 * @param {AnyGatewayEnv} [env='prod'] - Environment to use for the gateway
 * @param {string} [apiToken=''] - Token used for CB payment service authentication
 * @param {AnyGatewayMode} [mode='live'] - Mode for the Tokenex SDK (live/test)
 * @returns {GatewayHookResponse} Hook response containing loading state and configuration
 */

import {
  TokenexFormConfigType,
  TokenexModeType,
  TokenexSchemaConfigType,
  UseGatewayParams,
  AnyGatewayEnv,
  AnyGatewayMode,
} from '../@types';
import { ApiHosts, TokenexScriptUrl } from '../constants';
import { log } from '../utils/logger';

/**
 * @description Loads the Tokenex SDK script into the document.
 * @param {TokenexModeType} mode - The Tokenex mode ('live' or 'test').
 * @param {boolean} reload - Whether to force reload the script.
 * @returns {Promise<void>} Resolves when the script is loaded.
 */
const loadTokenexSDK = (mode: TokenexModeType = 'live', reload = false) =>
  new Promise<void>(resolve => {
    if (!reload && (window as any).TokenEx) {
      log('Tokenex already Loaded.');
      resolve();
    }

    const script = document.createElement('script');
    script.src = TokenexScriptUrl[mode] || TokenexScriptUrl.live;
    script.async = true;

    script.addEventListener('load', async () => {
      log('Tokenex loaded.');
      resolve();
    });

    document.body.appendChild(script);
  });

type FetchTokenexParams = {
  env: AnyGatewayEnv;
  apiToken: string;
  mode: AnyGatewayMode;
};

/**
 * @description Fetches the Tokenex configuration from the backend for the given environment and mode.
 * @param {FetchTokenexParams} params - Parameters for fetching Tokenex config.
 * @returns {Promise<TokenexFormConfigType['options'] | null>} The Tokenex config options or null.
 */
const fetchTokenexConfig = async ({
  env = 'prod',
  apiToken = '',
  mode = 'live',
}: FetchTokenexParams) => {
  const apiHost = ApiHosts[env].host;
  const authKeyUrl = `${apiHost}/payments-configuration/v1.0/tokenex/authentication_key`;

  try {
    const response = await fetch(authKeyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiToken}`,
        Origin: ApiHosts[env].origin,
      },
      body: JSON.stringify({
        origins: [ApiHosts[env].origin],
        mode: mode.toUpperCase(),
      }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    const {
      authentication_key: authKeys,
      client_id: tokenExID,
      timestamp,
    } = data;

    const tokenexConfig = (
      authKeys as Record<'authentication_key' | 'scheme', string>[]
    ).reduce<TokenexSchemaConfigType>((a, b) => {
      return {
        ...a,
        ...(b.scheme.includes('GUID') && {
          cvvAuthKey: b.authentication_key,
          cvvScheme: b.scheme,
        }),
        ...(!b.scheme.includes('GUID') && {
          cardAuthKey: b.authentication_key,
          cardScheme: b.scheme,
        }),
      };
    }, {} as TokenexSchemaConfigType);

    return {
      card: {
        origin: ApiHosts[env].origin,
        timestamp,
        tokenExID,
        tokenScheme: tokenexConfig.cardScheme,
        authenticationKey: tokenexConfig.cardAuthKey,
      },
      cvv: {
        origin: ApiHosts[env].origin,
        timestamp,
        tokenExID,
        tokenScheme: tokenexConfig.cvvScheme,
        authenticationKey: tokenexConfig.cvvAuthKey,
      },
    } as TokenexFormConfigType['options'];
  } catch (_error) {
    return null;
  }
};

/**
 * @description Loads the Tokenex configuration and SDK, returning the provider and options.
 * @param {Omit<UseGatewayParams, 'propertyId'>} params - Parameters for loading Tokenex.
 * @returns {Promise<TokenexFormConfigType | null>} The Tokenex form config or null.
 */
export const loadTokenex = async ({
  mode,
  env,
  apiToken,
}: Omit<UseGatewayParams, 'propertyId'>) => {
  return fetchTokenexConfig({
    mode,
    env,
    apiToken,
  })
    .then(data => {
      if (!data) {
        return null;
      }
      return loadTokenexSDK(mode)
        .then(() => {
          return {
            provider: 'tokenex',
            options: data,
          } as TokenexFormConfigType;
        })
        .catch(() => {
          return null;
        });
    })
    .catch(() => {
      return null;
    });
};
